import { useRevenueBySaleTypeReport } from "@gymflow/api";
import { DATE_FORMAT, PARAMETER_DATE_FORMAT_WITHOUT_TZ } from "@gymflow/common";
import { downloadCsv, formatCurrency } from "@gymflow/helpers";
import { RevenueReportItemDTO } from "@gymflow/types";
import { createColumnHelper, SortingState } from "@tanstack/react-table";
import { PaymentsSidebarProviderContext } from "apps/portal/src/components/UserMember/UserMemberPayments/components/PaymentsSidebarProviderContext";
import { usePortalRoutes } from "apps/portal/src/hooks";
import { DateTime } from "luxon";
import moment from "moment-timezone";
import qs from "qs";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DateRange } from "react-day-picker";
import { useHistory, useLocation } from "react-router";

import { useRevenueBySaleTypeReportCsv } from "../../../../../../libs/api/src/lib/hooks/report";
import { Badge, Button } from "../../../components/atoms";
import { ReportPagination } from "../../../components/molecules";
import { Table } from "../../../components/organisms";
import { Report } from "../../../components/organisms/Report";
import useQueryParam from "../../../hooks/useQueryParam";
import { useClubSettings } from "../../../providers";
import useGymflowModels from "../../../store";
import { UserMemberPage } from "../../UserMember";
import { formatDateRange, getDefaultDateRange } from "../helpers/dateHelpers";
import { PaymentMethodColumn } from "./components/PaymentMethodColumn";
import { TRANSACTION_TYPE_DISPLAY_MAP } from "./constants";
import { RevenueCategoryType } from "./types";

export function RevenueTotal() {
  const { openUserStaffPaymentsSidebar } = useContext(
    PaymentsSidebarProviderContext,
  );
  const history = useHistory();
  const { api } = useGymflowModels();
  const {
    club: { timezone, defaultCurrency },
  } = useClubSettings();
  const { createMemberLink } = usePortalRoutes();

  const location = useLocation();
  const searchParams = qs.parse(location.search, { ignoreQueryPrefix: true });
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState(25);
  const [currentSort, setCurrentSort] = useState<SortingState>([]);

  const { featureFlags } = useClubSettings();

  const [invoiceType] = useQueryParam("invoiceType", "string") as [
    RevenueCategoryType | undefined,
  ];
  const [dates, setDates] = useQueryParam("dates", "string") as any;
  const defaultDates = useMemo(
    () => ({
      startDate: moment().startOf("month").format(DATE_FORMAT),
      endDate: moment().format(DATE_FORMAT),
    }),
    [],
  );

  const defaultRange = useMemo(() => getDefaultDateRange(timezone), [timezone]);

  useEffect(() => {
    if (featureFlags.PORTAL_NEW_REPORTS_DATE_PICKER && !dates) {
      setDates(defaultRange);
    }
    if (!featureFlags.PORTAL_NEW_REPORTS_DATE_PICKER && !dates) {
      setDates(defaultDates);
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, isFetching } = useRevenueBySaleTypeReport({
    api,
    dateFrom: dates?.startDate,
    dateTo: dates?.endDate,
    tz: timezone,
    limit: pageSize,
    page: currentPage,
    invoiceType,
  });

  const downloadCsvMutation = useRevenueBySaleTypeReportCsv({
    api,
    tz: timezone,
  });

  const columnHelper = createColumnHelper<RevenueReportItemDTO>();
  const columnsDefinition = [
    columnHelper.accessor("transactionDate", {
      cell: (info) => (
        <div className="text-gray-600">
          {DateTime.fromISO(info.getValue())
            .setZone(timezone)
            .toLocaleString(DateTime.DATE_MED)}
        </div>
      ),
      header: "Transaction Date",
      enableSorting: false,
    }),
    columnHelper.accessor("invoiceNumber", {
      cell: (info) => {
        const invoiceNumber = info.getValue();
        const memberId = info.row.original.userMemberId;
        return (
          <Button
            intent="transparent"
            className="min-w-24 p-1 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 active:outline-none active:ring-0 active:ring-offset-0"
            onClick={() => {
              if (invoiceNumber && memberId) {
                openUserStaffPaymentsSidebar({ invoiceNumber, memberId });
              }
            }}
          >
            <div className="text-gray-600 hover:text-[#6186db]">
              {info.getValue()}
            </div>
          </Button>
        );
      },
      header: "Invoice Number",
      enableSorting: false,
    }),
    columnHelper.accessor("item", {
      cell: (info) => {
        const text =
          info.getValue() === "LATE_CANCELLATION"
            ? "Late Cancellation"
            : info.getValue();
        return <div className="text-gray-600">{text}</div>;
      },
      header: "Item / Order",
      enableSorting: false,
    }),
    columnHelper.accessor(
      (row) => {
        return {
          transactionAmount: row.transactionAmount,
          latePayment: row.latePayment,
        };
      },
      {
        cell: (info) => (
          <div className="flex gap-x-2">
            <div className="text-gray-600">
              {formatCurrency(
                info.getValue().transactionAmount,
                defaultCurrency,
              )}
            </div>
            {info.getValue().latePayment && (
              <Badge intent="warning">Late Payment</Badge>
            )}
          </div>
        ),
        header: "Amount Inc Tax",
        footer: () =>
          `Total: ${
            data?.total !== undefined
              ? formatCurrency(data.total, defaultCurrency)
              : ""
          }`,
        enableSorting: false,
      },
    ),
    columnHelper.accessor((row) => row, {
      header: "Payment Method",
      enableSorting: false,
      cell: (row) => <PaymentMethodColumn row={row} />,
    }),
    columnHelper.accessor("invoiceType", {
      cell: (info) => {
        const value = info.getValue();
        const displayText = value.toLowerCase().replace("_", " ");

        return <div className="capitalize text-gray-600">{displayText}</div>;
      },
      header: "Category",
      enableSorting: false,
    }),
    columnHelper.accessor("transactionType", {
      cell: (info) => {
        const value = info.getValue();
        const displayText =
          TRANSACTION_TYPE_DISPLAY_MAP[value] ||
          value.toLowerCase().replace("_", " ");

        return <div className="capitalize text-gray-600">{displayText}</div>;
      },
      header: "Type",
      enableSorting: false,
    }),
    columnHelper.accessor(
      (row) => {
        return {
          soldTo: row.soldTo,
          userMemberId: row.userMemberId,
        };
      },
      {
        cell: (info) => {
          const { userMemberId, soldTo } = info.getValue();
          return (
            <a
              className="text-gray-600"
              href={createMemberLink(userMemberId, UserMemberPage.Payments)}
            >
              {soldTo}
            </a>
          );
        },
        header: "Purchased By",
        enableSorting: false,
      },
    ),
    columnHelper.accessor("soldBy", {
      cell: (info) => {
        return (
          <div className="text-gray-600">{info.getValue() ?? "Unassisted"}</div>
        );
      },
      header: "Sold By",
      enableSorting: false,
    }),
  ];

  const handleDateRangeChange = useCallback(
    (newDate: any) => {
      if (newDate) {
        setDates({
          startDate: moment(newDate.startDate, DATE_FORMAT).format(
            PARAMETER_DATE_FORMAT_WITHOUT_TZ,
          ),
          endDate: moment(newDate.endDate, DATE_FORMAT)
            .endOf("day")
            .format(PARAMETER_DATE_FORMAT_WITHOUT_TZ),
        });
      }
    },
    [setDates],
  );

  const onDateSelectionApply = useCallback(
    (
      newDate: DateRange | { startDate: string; endDate: string } | undefined,
    ) => {
      setDates(formatDateRange(newDate));
      setDates(formatDateRange(newDate));
    },
    [setDates],
  );

  return (
    <Report
      title={
        (
          {
            TOTAL: "Total Revenue",
            MEMBERSHIP: "Membership Revenue",
            CREDIT_PACK: "Credit Pack Revenue",
            PRODUCT_ORDER: "Product Revenue",
          } as const
        )[invoiceType ?? "TOTAL"]
      }
      description={
        (
          {
            TOTAL:
              "List of all successful sales and refunds within the specified date period. Revenue counts when the transaction was made not when it was invoiced, revenue does not include overdue invoices.",
            MEMBERSHIP:
              "List of all successful membership sales and refunds within the specified date period. Revenue counts when the transaction was made not when it was invoiced, revenue does not include overdue invoices.",
            CREDIT_PACK:
              "List of all successful credit pack sales and refunds within the specified date period. Revenue counts when the transaction was made not when it was invoiced, revenue does not include overdue invoices.",
            PRODUCT_ORDER:
              "List of all successful product sales and refunds within the specified date period. Revenue counts when the transaction was made not when it was invoiced, revenue does not include overdue invoices.",
          } as const
        )[invoiceType ?? "TOTAL"]
      }
      onDownloadClick={async () => {
        const data = await downloadCsvMutation.mutateAsync({
          invoiceType,
          dateFrom: dates?.startDate,
          dateTo: dates?.endDate,
        });
        downloadCsv(
          data,
          `${
            (
              {
                TOTAL: "TotalRevenue",
                MEMBERSHIP: "MembershipRevenue",
                CREDIT_PACK: "CreditPackRevenue",
                PRODUCT_ORDER: "ProductRevenue",
              } as const
            )[invoiceType ?? "TOTAL"]
          }.csv`,
        );
      }}
      handleDateRangeChange={handleDateRangeChange}
      onDateSelectionApply={onDateSelectionApply}
      dateRange={
        dates?.startDate && dates?.endDate
          ? featureFlags.PORTAL_NEW_REPORTS_DATE_PICKER
            ? {
                from: new Date(dates.startDate),
                to: new Date(dates.endDate),
              }
            : {
                startDate: moment(
                  dates.startDate,
                  PARAMETER_DATE_FORMAT_WITHOUT_TZ,
                ).format(DATE_FORMAT),
                endDate: moment(
                  dates.endDate,
                  PARAMETER_DATE_FORMAT_WITHOUT_TZ,
                ).format(DATE_FORMAT),
              }
          : undefined
      }
      close={() => {
        history.push({
          pathname: searchParams["backRoute"] as string,
          search: qs.stringify({
            dates: {
              startDate: moment(
                dates.startDate,
                PARAMETER_DATE_FORMAT_WITHOUT_TZ,
              ).format(DATE_FORMAT),
              endDate: moment(
                dates.endDate,
                PARAMETER_DATE_FORMAT_WITHOUT_TZ,
              ).format(DATE_FORMAT),
            },
          }),
        });
      }}
      table={
        <>
          <Table
            data={data?.data.content ?? []}
            columns={columnsDefinition}
            pageCount={data?.data.totalPages}
            pageIndex={data?.data.number}
            onSortingChange={setCurrentSort}
            sort={currentSort}
            isFetching={isFetching}
            pageSize={pageSize}
            rowClassName="h-14"
          />
          <div className="border-t border-gray-300">
            <ReportPagination
              pageCount={data?.data.totalPages as number}
              currentPage={data?.data.number as number}
              onPageChange={(newPage) => {
                setCurrentPage(newPage);
              }}
              pageSize={pageSize}
              setPageSize={(newValue) => {
                setPageSize(newValue);
                setCurrentPage(0);
              }}
            />
          </div>
        </>
      }
    />
  );
}
